import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import { getAuthorName } from "@/pages/(app)/returns/utils/get-author-name.ts";
import ActivityContentWrapper from "@/pages/(app)/returns/view/components/return-activity/contents/activity-content-wrapper.tsx";
import ActivityIconWrapper from "@/pages/(app)/returns/view/components/return-activity/contents/activity-icon-wrapper.tsx";
import { ActivityItemHeader } from "@/pages/(app)/returns/view/components/return-activity/contents/activity-item-header.tsx";
import ActivityWrapper from "@/pages/(app)/returns/view/components/return-activity/contents/activity-wrapper.tsx";
import { ReturnRequestLogDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { UserPen } from "lucide-react";

type CustomerUpdateField = "email" | "first_name" | "last_name" | "phone";

type CustomerUpdateParams = {
  changes?: {
    field: CustomerUpdateField;
    oldValue: string;
    newValue: string;
  }[];
};

type UpdateCustomerActivityProps = {
  activity: ReturnRequestLogDto;
};

export default function UpdateCustomerActivity({
  activity,
}: UpdateCustomerActivityProps) {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();

  const author = shop.users.find(
    (user) => user.userId === activity.author,
  )?.user;
  const authorName = getAuthorName(author);

  const { changes = [] } = (activity.params ?? {}) as CustomerUpdateParams;

  const fieldLabels: Record<CustomerUpdateField, string> = {
    email: t("returnRequest.customer.email"),
    first_name: t("misc.firstName"),
    last_name: t("misc.name"),
    phone: t("address.phone"),
  };

  return (
    <ActivityWrapper>
      <ActivityIconWrapper>
        <UserPen className="size-4" />
      </ActivityIconWrapper>
      <ActivityContentWrapper>
        <ActivityItemHeader title="" timestamp={activity.date}>
          <span className="font-bold">{authorName}</span>
          {t("returnDetail.modifiedCustomer")}
        </ActivityItemHeader>
        <div className="flex flex-col gap-0.5 text-xs text-muted-foreground">
          {changes.map((change) => (
            <div key={change.field} className="flex items-center gap-1">
              <span className="font-medium">{fieldLabels[change.field]}</span>
              <span className="line-through">{change.oldValue || "—"}</span>
              <span>→</span>
              <span>{change.newValue || "—"}</span>
            </div>
          ))}
        </div>
      </ActivityContentWrapper>
    </ActivityWrapper>
  );
}
